import { Temporal } from 'temporal-polyfill';
import { IMetricsDataModel } from '../../../../data-models/metrics.data-model';
import { CalculatedMetricsDataModel } from './MetricsCalculator';

type FilteredMetricsCalculator = (
  metrics: CalculatedMetricsDataModel[],
  filteredData: IMetricsDataModel[],
  asOfDate: Temporal.PlainDate
) => { metrics: CalculatedMetricsDataModel[]; filteredMetrics: CalculatedMetricsDataModel[] };

/**
 * Fixed point for the grid's publish→recompute feedback: the grid republishes the rows it just
 * ingested (new identities, same content), and tenant calculators may clone rows on every call
 * (greycroft), so raw output identity always changes and the grid would re-ingest forever. The
 * calculator's output is fully determined by (base rows reference, filtered companyId sequence,
 * as-of date) — value changes upstream always replace the base reference — so an echoed input
 * returns the previous result object and the grid sees no rowData change.
 *
 * Cache-key contract: every tenant calculateMetricsOnFilteredData must consume filteredData
 * through row membership (companyIds) only — all current ones do. An override that reads row
 * VALUES from filteredData needs this key widened, or it will serve stale results.
 */
export function createStableFilteredMetricsCalculator(
  calculate: FilteredMetricsCalculator
): FilteredMetricsCalculator {
  let lastMetrics: CalculatedMetricsDataModel[] | null = null;
  let lastMembershipKey: string | null = null;
  let lastDateKey: string | null = null;
  let lastResult: ReturnType<FilteredMetricsCalculator> | null = null;

  return (metrics, filteredData, asOfDate) => {
    const membershipKey = filteredData.map((m) => m.companyId).join(',');
    const dateKey = asOfDate.toString();

    if (
      lastResult !== null &&
      lastMetrics === metrics &&
      lastMembershipKey === membershipKey &&
      lastDateKey === dateKey
    ) {
      return lastResult;
    }

    lastMetrics = metrics;
    lastMembershipKey = membershipKey;
    lastDateKey = dateKey;
    lastResult = calculate(metrics, filteredData, asOfDate);
    return lastResult;
  };
}
